/**
 * Pestaña "Facturación electrónica" de Pagos (iteración 10, ADR 0043): estado del proveedor y de
 * la cola, comprobación de la cuenta de Alegra (empresa, numeraciones, ítems, consumidor final),
 * ejecución del ciclo y lista de documentos con reintento y omisión.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../auth/auth.tsx';
import { ConfirmDialog } from '../components/confirm-dialog.tsx';
import { DataTable } from '../components/data-table.tsx';
import { Alert, Badge, ErrorBox, Kpi, Loading } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.tsx';
import { dateTime, money } from '../lib/format.ts';
import type { Tone } from '../lib/status.ts';
import type { Items } from '../lib/types.ts';
import { useMutation, useQuery } from '../lib/use-query.ts';

type EInvoiceStatus = 'PENDING' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'ERROR' | 'SKIPPED';
const STATUSES: EInvoiceStatus[] = ['PENDING', 'SENT', 'ACCEPTED', 'REJECTED', 'ERROR', 'SKIPPED'];
const TONES: Record<EInvoiceStatus, Tone> = {
  PENDING: 'neutral',
  SENT: 'info',
  ACCEPTED: 'ok',
  REJECTED: 'danger',
  ERROR: 'warning',
  SKIPPED: 'neutral',
};

interface Status {
  provider: string;
  configured: boolean;
  reason: string | null;
  settings: {
    enabled: boolean;
    since: string;
    sandboxPayments: boolean;
    sendEmail: boolean;
    maxAttempts: number;
  };
  alegra: {
    numberTemplateId: number | null;
    creditNoteTemplateId: number | null;
    items: Record<string, number>;
    finalConsumerContactId: number | null;
    paymentMethod: string;
  };
  counts: Record<EInvoiceStatus, number>;
  lastAcceptedAt: string | null;
  oldestPendingAt: string | null;
}
interface Diagnostics {
  ok: boolean;
  provider: string;
  credentials: {
    user: string;
    scheme: 'basic' | 'bearer';
    tokenLength: number;
    tokenShape: 'jwt' | 'token' | 'suspicious';
    tokenPreview: string;
  } | null;
  company: { name: string | null; identification: string | null; email: string | null } | null;
  numberTemplates: {
    id: string;
    prefix: string | null;
    name: string | null;
    isElectronic: boolean;
    nextNumber: string | null;
    documentType: string | null;
  }[];
  items: Record<string, { id: string; name: string; taxes: string[] } | null>;
  availableItems: { id: string; name: string; taxes: string[] }[];
  finalConsumer: { id: string; name: string; identification: string | null } | null;
  problems: string[];
  comparison?: {
    userMatch: 'exact' | 'case' | 'different';
    tokenMatch: boolean;
    tokenLengthDifference: number;
  };
}
interface EInvoice {
  id: string;
  kind: 'INVOICE' | 'CREDIT_NOTE';
  status: EInvoiceStatus;
  session_id: string;
  number: string | null;
  cufe: string | null;
  pdf_url: string | null;
  buyer: {
    name: string;
    documentType: string | null;
    documentNumber: string | null;
    email: string | null;
  };
  currency: string;
  total_minor: number | string;
  attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
  warnings: string[];
  email_sent_at: string | null;
  created_at: string;
}
interface Cycle {
  enqueued: { invoices: number; creditNotes: number };
  issued: number;
  accepted: number;
  rejected: number;
  errors: number;
}

export function EInvoicingTab({
  canOperate,
  canConfigure,
  onOpenSession,
}: {
  canOperate: boolean;
  /** Puede guardar la configuración y mover los interruptores (params:write_sensitive). */
  canConfigure: boolean;
  onOpenSession: (sessionId: string) => void;
}) {
  const { t, td, locale } = useI18n();
  const api = useApi();
  const [filter, setFilter] = useState<EInvoiceStatus | ''>('');
  const [check, setCheck] = useState<Diagnostics | null>(null);
  const [checking, setChecking] = useState(false);
  const [probeUser, setProbeUser] = useState('');
  const [probeToken, setProbeToken] = useState('');
  const [probed, setProbed] = useState(false);
  const [cycle, setCycle] = useState<Cycle | null>(null);
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    requireReason: boolean;
    action: (reason: string) => Promise<unknown>;
  } | null>(null);
  const mutation = useMutation();
  const status = useQuery(() => api.get<Status>('/billing/einvoicing'), [], { refreshMs: 30_000 });
  const documents = useQuery(
    () =>
      api.get<Items<EInvoice>>('/billing/einvoices', { limit: 200, status: filter || undefined }),
    [filter],
    { refreshMs: 30_000 },
  );
  const refresh = () => {
    void status.reload();
    void documents.reload();
  };
  const runCheck = async () => {
    setChecking(true);
    setCheck(null);
    setProbed(false);
    try {
      setCheck(
        (await mutation.run(() => api.get<Diagnostics>('/billing/einvoicing/check'))) ?? null,
      );
    } finally {
      setChecking(false);
    }
  };
  // Prueba otras credenciales sin guardarlas: el resultado se muestra como el de Comprobar cuenta.
  const runProbe = async () => {
    setChecking(true);
    setCheck(null);
    try {
      const result = await mutation.run(() =>
        api.post<Diagnostics>('/billing/einvoicing/check', { user: probeUser, token: probeToken }),
      );
      setCheck(result ?? null);
      setProbed(result !== undefined);
      if (result) setProbeToken('');
    } finally {
      setChecking(false);
    }
  };
  const runCycle = async () => {
    const result = await mutation.run(() => api.post<Cycle>('/billing/einvoicing/run', {}));
    if (result) setCycle(result);
    refresh();
  };
  const act = async (reason: string) => {
    if (!confirm) return;
    const result = await mutation.run(() => confirm.action(reason));
    setConfirm(null);
    if (result !== undefined) refresh();
  };
  const data = status.data;
  const cop = (value: number | string, currency: string) => money(Number(value), currency, locale);

  return (
    <>
      <div className="card">
        <div className="row between">
          <h2>{t('ein.title')}</h2>
          <div className="row">
            <button
              type="button"
              className="small"
              onClick={() => void runCheck()}
              disabled={checking}
            >
              {checking ? t('ein.checking') : t('ein.check')}
            </button>
            {canOperate ? (
              <button
                type="button"
                className="small"
                onClick={() => void runCycle()}
                disabled={mutation.busy}
              >
                {t('ein.run')}
              </button>
            ) : null}
          </div>
        </div>
        <p className="muted small">{t('ein.help')}</p>
        <ErrorBox error={status.error} />
        <ErrorBox error={mutation.error} />
        {mutation.message ? <Alert tone="ok">{mutation.message}</Alert> : null}
        {status.loading && !data ? <Loading /> : null}
        {data ? (
          <>
            {!data.configured ? (
              <Alert tone="warning">{t('ein.noProvider', { reason: data.reason ?? '' })}</Alert>
            ) : null}
            <p>
              <Badge tone={data.settings.enabled ? 'ok' : 'neutral'}>
                {data.settings.enabled ? t('ein.enabled') : t('ein.disabled')}
              </Badge>{' '}
              <Badge tone="neutral">
                {t('ein.provider')}: {data.provider}
              </Badge>{' '}
              <Badge tone="neutral">
                {t('ein.since')}: {data.settings.since.slice(0, 10)}
              </Badge>{' '}
              {data.settings.sandboxPayments ? (
                <Badge tone="warning">{t('ein.sandbox')}</Badge>
              ) : null}{' '}
              {data.settings.sendEmail ? <Badge tone="neutral">{t('ein.sendEmail')}</Badge> : null}
            </p>
            <div className="grid cols-3">
              {STATUSES.map((s) => (
                <Kpi key={s} label={td(`ein.status.${s}`)} value={data.counts[s] ?? 0} />
              ))}
            </div>
            <p className="muted small">
              {t('ein.lastAccepted')}:{' '}
              {data.lastAcceptedAt ? dateTime(data.lastAcceptedAt, locale) : '—'} ·{' '}
              {t('ein.oldestPending')}:{' '}
              {data.oldestPendingAt ? dateTime(data.oldestPendingAt, locale) : '—'}
            </p>
          </>
        ) : null}
        {cycle ? (
          <Alert tone="info">
            {t('ein.ran', {
              enqueued: String(cycle.enqueued.invoices + cycle.enqueued.creditNotes),
              issued: String(cycle.issued),
              accepted: String(cycle.accepted),
              rejected: String(cycle.rejected),
              errors: String(cycle.errors),
            })}
          </Alert>
        ) : null}
        {check ? (
          <div className="mt">
            {probed ? <Alert tone="info">{t('ein.probeResult')}</Alert> : null}
            {probed && check.comparison ? (
              <Alert
                tone={
                  check.comparison.userMatch === 'exact' && check.comparison.tokenMatch
                    ? 'ok'
                    : 'warning'
                }
              >
                {t('ein.compareTitle')} {td(`ein.compareUser.${check.comparison.userMatch}`)}
                {' · '}
                {check.comparison.tokenMatch
                  ? t('ein.compareTokenSame')
                  : t('ein.compareTokenDiff', {
                      length:
                        check.comparison.tokenLengthDifference === 0
                          ? t('ein.compareSameLength')
                          : t('ein.compareLengthDiff', {
                              delta: String(check.comparison.tokenLengthDifference),
                            }),
                    })}
              </Alert>
            ) : null}
            {check.ok ? (
              <Alert tone="ok">{t('ein.checkOk')}</Alert>
            ) : (
              <Alert tone="warning">
                <strong>{t('ein.problems')}</strong>
                <ul>
                  {check.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {check.credentials ? (
              <p className="muted small">
                {t('ein.credentials')}: {check.credentials.user} ·{' '}
                {check.credentials.scheme === 'bearer'
                  ? t('ein.schemeBearer')
                  : t('ein.schemeBasic')}{' '}
                ·{' '}
                {t('ein.tokenInfo', {
                  length: String(check.credentials.tokenLength),
                  shape: td(`ein.tokenShape.${check.credentials.tokenShape}`),
                })}{' '}
                · {check.credentials.tokenPreview}
              </p>
            ) : null}
            {check.company ? (
              <p>
                <strong>{t('ein.company')}</strong>: {check.company.name ?? '—'} · NIT{' '}
                {check.company.identification ?? '—'} · {check.company.email ?? '—'}
              </p>
            ) : null}
            {check.numberTemplates.length > 0 ? (
              <p>
                <strong>{t('ein.numberTemplates')}</strong>:{' '}
                {check.numberTemplates
                  .map(
                    (n) =>
                      `#${n.id} ${n.prefix ?? ''} ${n.name ?? ''} (${n.isElectronic ? t('ein.electronic') : t('ein.notElectronic')}${n.nextNumber ? `, ${t('ein.next')} ${n.nextNumber}` : ''})`,
                  )
                  .join(' · ')}
              </p>
            ) : null}
            {Object.keys(check.items).length > 0 ? (
              <p>
                <strong>{t('ein.items')}</strong>:{' '}
                {Object.entries(check.items)
                  .map(([key, item]) =>
                    item
                      ? `${key} → #${item.id} ${item.name} (${item.taxes.length ? `${t('ein.taxes')}: ${item.taxes.join(', ')}` : t('ein.noTaxes')})`
                      : `${key} → ${t('ein.itemMissing')}`,
                  )
                  .join(' · ')}
              </p>
            ) : null}
            <p>
              <strong>{t('ein.finalConsumer')}</strong>:{' '}
              {check.finalConsumer
                ? `#${check.finalConsumer.id} ${check.finalConsumer.name} (${check.finalConsumer.identification ?? ''})`
                : t('ein.finalConsumerMissing')}
            </p>
          </div>
        ) : null}
        {canOperate && data?.provider === 'alegra' ? (
          <details className="mt">
            <summary>{t('ein.probeTitle')}</summary>
            <p className="muted small">{t('ein.probeHelp')}</p>
            <div className="grid cols-3">
              <label className="field" htmlFor="ein-probe-user">
                <span>{t('ein.probeUser')}</span>
                <input
                  id="ein-probe-user"
                  type="email"
                  autoComplete="off"
                  value={probeUser}
                  onChange={(e) => setProbeUser(e.target.value)}
                />
              </label>
              <label className="field" htmlFor="ein-probe-token">
                <span>{t('ein.probeToken')}</span>
                <input
                  id="ein-probe-token"
                  type="password"
                  autoComplete="off"
                  value={probeToken}
                  onChange={(e) => setProbeToken(e.target.value)}
                />
              </label>
              <div className="field">
                <span>&nbsp;</span>
                <button
                  type="button"
                  className="small"
                  onClick={() => void runProbe()}
                  disabled={checking || probeUser.trim().length < 3 || probeToken.trim().length < 8}
                >
                  {t('ein.probeRun')}
                </button>
              </div>
            </div>
          </details>
        ) : null}
      </div>

      {data ? (
        <SetupPanel
          status={data}
          check={check}
          canConfigure={canConfigure}
          busy={mutation.busy}
          onSave={async (input) => {
            const result = await mutation.run(
              () => api.put<Status>('/billing/einvoicing/settings', input),
              t('ein.saved'),
            );
            if (result) refresh();
            return result !== undefined;
          }}
          onConfirm={(title, message, action) =>
            setConfirm({ title, message, requireReason: false, action })
          }
        />
      ) : null}

      <div className="card">
        <div className="row between mb">
          <h2>{t('ein.documents')}</h2>
          <select value={filter} onChange={(e) => setFilter(e.target.value as EInvoiceStatus | '')}>
            <option value="">{t('ein.all')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {td(`ein.status.${s}`)}
              </option>
            ))}
          </select>
        </div>
        {documents.loading && !documents.data ? <Loading /> : null}
        <ErrorBox error={documents.error} />
        <DataTable
          rows={documents.data?.items ?? []}
          rowKey={(d) => d.id}
          columns={[
            { key: 'at', header: t('pay.received'), render: (d) => dateTime(d.created_at, locale) },
            { key: 'kind', header: t('ein.kind'), render: (d) => td(`ein.kind.${d.kind}`) },
            {
              key: 'status',
              header: t('app.status'),
              render: (d) => <Badge tone={TONES[d.status]}>{td(`ein.status.${d.status}`)}</Badge>,
            },
            {
              key: 'buyer',
              header: t('ein.buyer'),
              render: (d) =>
                `${d.buyer.name}${d.buyer.documentNumber ? ` · ${d.buyer.documentType ?? ''} ${d.buyer.documentNumber}` : ''}`,
            },
            {
              key: 'total',
              header: t('ein.total'),
              render: (d) => cop(d.total_minor, d.currency),
              align: 'right',
            },
            {
              key: 'number',
              header: t('ein.number'),
              render: (d) => (
                <>
                  {d.number ?? '—'}
                  {d.pdf_url ? (
                    <>
                      {' '}
                      <a href={d.pdf_url} target="_blank" rel="noreferrer">
                        {t('ein.pdf')}
                      </a>
                    </>
                  ) : null}
                  {d.cufe ? (
                    <div className="muted small" title={d.cufe}>
                      {t('ein.cufe')} {d.cufe.slice(0, 10)}…
                    </div>
                  ) : null}
                </>
              ),
            },
            {
              key: 'attempts',
              header: t('ein.attempts'),
              render: (d) =>
                `${d.attempts}${d.next_attempt_at && (d.status === 'ERROR' || d.status === 'PENDING') ? ` · ${t('ein.nextAttempt')} ${dateTime(d.next_attempt_at, locale)}` : ''}`,
            },
            {
              key: 'error',
              header: t('ein.error'),
              render: (d) => (
                <span
                  className="small"
                  title={[d.last_error, ...d.warnings].filter(Boolean).join('\n')}
                >
                  {d.last_error ?? (d.warnings.length ? d.warnings[0] : '—')}
                </span>
              ),
            },
            {
              key: 'actions',
              header: t('app.actions'),
              render: (d) => (
                <div className="row">
                  <button
                    type="button"
                    className="small"
                    onClick={() => onOpenSession(d.session_id)}
                  >
                    {t('ein.session')}
                  </button>
                  {canOperate &&
                  (d.status === 'ERROR' || d.status === 'REJECTED' || d.status === 'SKIPPED') ? (
                    <button
                      type="button"
                      className="small"
                      onClick={() =>
                        setConfirm({
                          title: t('ein.retryTitle'),
                          message: t('ein.retryHelp'),
                          requireReason: false,
                          action: () => api.post(`/billing/einvoices/${d.id}/retry`, {}),
                        })
                      }
                    >
                      {t('ein.retry')}
                    </button>
                  ) : null}
                  {canOperate &&
                  (d.status === 'PENDING' || d.status === 'ERROR' || d.status === 'REJECTED') ? (
                    <button
                      type="button"
                      className="small danger"
                      onClick={() =>
                        setConfirm({
                          title: t('ein.skipTitle'),
                          message: t('ein.skipHelp'),
                          requireReason: true,
                          action: (reason) =>
                            api.post(`/billing/einvoices/${d.id}/skip`, { reason }),
                        })
                      }
                    >
                      {t('ein.skip')}
                    </button>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </div>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.title ?? ''}
        message={confirm?.message}
        requireReason={confirm?.requireReason}
        busy={mutation.busy}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => void act(reason)}
      />
    </>
  );
}

/**
 * Configuración guiada: ítem de las líneas y numeraciones elegidos entre los de la cuenta (tras
 * Comprobar cuenta), e interruptores de emisión y de cobros de sandbox con confirmación.
 */
function SetupPanel({
  status,
  check,
  canConfigure,
  busy,
  onSave,
  onConfirm,
}: {
  status: Status;
  check: Diagnostics | null;
  /** Puede guardar la configuración y mover los interruptores (params:write_sensitive). */
  canConfigure: boolean;
  busy: boolean;
  onSave: (input: Record<string, unknown>) => Promise<boolean>;
  onConfirm: (title: string, message: string, action: () => Promise<unknown>) => void;
}) {
  const { t } = useI18n();
  const [itemId, setItemId] = useState('');
  const [invoiceTemplate, setInvoiceTemplate] = useState('0');
  const [creditTemplate, setCreditTemplate] = useState('0');
  const items = check?.availableItems ?? [];
  const templates = check?.numberTemplates ?? [];
  // Numeraciones electrónicas por tipo de documento o, si Alegra no lo informa, por el nombre.
  const invoiceTemplates = templates.filter(
    (n) =>
      n.isElectronic &&
      (n.documentType
        ? /invoice/i.test(n.documentType)
        : /factura/i.test(`${n.prefix ?? ''} ${n.name ?? ''}`) &&
          !/nota|proveedor|compra/i.test(`${n.prefix ?? ''} ${n.name ?? ''}`)),
  );
  const creditTemplates = templates.filter(
    (n) =>
      n.isElectronic &&
      (n.documentType
        ? /credit/i.test(n.documentType)
        : /cr[eé]dito/i.test(`${n.prefix ?? ''} ${n.name ?? ''}`)),
  );

  // Valores vigentes y, si no hay nada configurado, la única opción posible de la cuenta: el único
  // ítem sin impuestos y las únicas numeraciones electrónicas de factura y de nota crédito.
  useEffect(() => {
    const current = status.alegra.items.DEFAULT;
    const taxFree = items.filter((i) => i.taxes.length === 0);
    setItemId(current ? String(current) : taxFree.length === 1 ? (taxFree[0]?.id ?? '') : '');
    setInvoiceTemplate(
      String(
        status.alegra.numberTemplateId ||
          (invoiceTemplates.length === 1 ? (invoiceTemplates[0]?.id ?? 0) : 0),
      ),
    );
    setCreditTemplate(
      String(
        status.alegra.creditNoteTemplateId ||
          (creditTemplates.length === 1 ? (creditTemplates[0]?.id ?? 0) : 0),
      ),
    );
  }, [status, items, templates]);

  const save = () =>
    void onSave({
      ...(itemId ? { itemId: Number(itemId) } : {}),
      numberTemplateId: Number(invoiceTemplate) || 0,
      creditNoteTemplateId: Number(creditTemplate) || 0,
    });
  const toggle = (key: 'enabled' | 'sandboxPayments', next: boolean) => {
    const title =
      key === 'enabled'
        ? next
          ? t('ein.enableTitle')
          : t('ein.disableTitle')
        : next
          ? t('ein.sandboxTitle')
          : t('ein.sandboxOffTitle');
    const message =
      key === 'enabled'
        ? next
          ? t('ein.enableHelp')
          : t('ein.disableHelp')
        : next
          ? t('ein.sandboxHelp')
          : '';
    onConfirm(title, message, () => onSave({ [key]: next }));
  };
  const templateOptions = (list: typeof templates) =>
    list.map((n) => (
      <option key={n.id} value={n.id}>
        #{n.id} {n.prefix ?? ''} {n.name ?? ''}
        {n.nextNumber ? ` · ${t('ein.next')} ${n.nextNumber}` : ''}
      </option>
    ));

  return (
    <div className="card">
      <h2>{t('ein.setup')}</h2>
      <p className="muted small">{t('ein.setupHelp')}</p>
      {!check ? <Alert tone="info">{t('ein.checkFirst')}</Alert> : null}
      <div className="grid cols-3">
        <label className="field" htmlFor="ein-item">
          <span>{t('ein.item')}</span>
          {items.length > 0 ? (
            <select id="ein-item" value={itemId} onChange={(e) => setItemId(e.target.value)}>
              <option value="">{t('ein.itemNone')}</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} (#{i.id}){i.taxes.length ? ` · ${i.taxes.join(', ')}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="ein-item"
              type="number"
              min={1}
              placeholder={t('ein.manualId')}
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
            />
          )}
          <small className="muted">{t('ein.itemHelp')}</small>
        </label>
        <label className="field">
          <span>{t('ein.invoiceTemplate')}</span>
          <select value={invoiceTemplate} onChange={(e) => setInvoiceTemplate(e.target.value)}>
            <option value="0">{t('ein.preferred')}</option>
            {templateOptions(invoiceTemplates)}
          </select>
        </label>
        <label className="field">
          <span>{t('ein.creditTemplate')}</span>
          <select value={creditTemplate} onChange={(e) => setCreditTemplate(e.target.value)}>
            <option value="0">{t('ein.preferred')}</option>
            {templateOptions(creditTemplates)}
          </select>
        </label>
      </div>
      {canConfigure ? (
        <div className="row">
          <button type="button" className="primary" onClick={save} disabled={busy}>
            {t('ein.save')}
          </button>
        </div>
      ) : null}
      <h3 className="mt">{t('ein.switches')}</h3>
      <label className="row">
        <input
          type="checkbox"
          checked={status.settings.sandboxPayments}
          disabled={!canConfigure || busy}
          onChange={(e) => toggle('sandboxPayments', e.target.checked)}
        />
        <span>{t('ein.toggleSandbox')}</span>
      </label>
      <label className="row">
        <input
          type="checkbox"
          checked={status.settings.enabled}
          disabled={!canConfigure || busy}
          onChange={(e) => toggle('enabled', e.target.checked)}
        />
        <span>{t('ein.toggleEnabled')}</span>
      </label>
    </div>
  );
}
